// =============================================================================
// src/lib/ridesApi.js - ride help records (appointment_rides) and Ride booked.
// Reads follow RLS (staff of the practice; a patient sees their own rides).
// =============================================================================
import { supabase } from "./supabaseClient";
import { ALL_DRIVER_ROLES } from "./ridesCore";
import { runEligibilityCheck } from "./eligibility";

const COLS = "id, appointment_id, task_id, status, pickup_at, pickup_window_end, return_trip_ref, pickup_address, en_route_at, en_route_eta, patient_ready, standing, combined_with, trip_ref, vendor, booked_at, provider_type, driver_id, care_visit_id, return_mode, return_at, return_called_at, return_task_id, issue, issue_task_id, picked_up_at, arrived_at, miles, outcome, minutes_late, dropped_off_at, home_driver_id, home_ack, home_source, home_not_needed_reason";

// With the visit's start time, which seeds the pickup time.
// The booking task, the "Call for the return ride" task or the "Ride did not
// come" task all open the same ride.
export async function fetchRideForTask(taskId) {
  const { data, error } = await supabase.from("appointment_rides").select(COLS + ", appointments(start_at, status)")
    .or("task_id.eq." + taskId + ",return_task_id.eq." + taskId + ",issue_task_id.eq." + taskId).limit(1).maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function fetchRideForAppointment(appointmentId) {
  const { data, error } = await supabase.from("appointment_rides").select(COLS).eq("appointment_id", appointmentId).maybeSingle();
  if (error) throw error;
  return data || null;
}

// Records the booking and completes the task (closes it everywhere).
export async function markRideBooked(taskId, { pickupAt = null, tripRef = "", vendor = "" } = {}) {
  const { data, error } = await supabase.rpc("ride_mark_booked", {
    p_task_id: taskId, p_pickup_at: pickupAt, p_trip_ref: tripRef || null, p_vendor: vendor || null,
  });
  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Transportation profile and care team drivers (20261226100000).
// ---------------------------------------------------------------------------
const PROFILE_COLS = "patient_id, ride_need, usual_source, driver_id, wheelchair, escort, notes, pickup_address, updated_at, mobility, oxygen, service_animal, car_seat, car_seat_age, bariatric, door_to_door, interpreter, standing_vendor, standing_ref, standing_until";

export async function fetchTransport(patientId) {
  const { data, error } = await supabase.from("patient_transport").select(PROFILE_COLS).eq("patient_id", patientId).maybeSingle();
  if (error) throw error;
  return data || null;
}

// Saves the profile. "always" also sets up rides for upcoming visits that
// have none; the result says how many (rides_created).
export async function saveTransport(patientId, p) {
  const { data, error } = await supabase.rpc("patient_transport_save", {
    p_patient_id: patientId,
    p_need: p.ride_need,
    p_source: p.usual_source,
    p_driver_id: p.driver_id || null,
    p_wheelchair: !!p.wheelchair,
    p_escort: !!p.escort,
    p_notes: (p.notes || "").trim() || null,
  });
  if (error) throw error;
  return data || {};
}

// Rides block 5: mobility and extras (patient_transport_set_needs). The
// result says whether a care team car can still carry the patient.
export async function setTransportNeeds(patientId, needs) {
  const { data, error } = await supabase.rpc("patient_transport_set_needs", { p_patient_id: patientId, p_needs: needs });
  if (error) throw error;
  return data || {};
}

// The vendor's pickup window end and the return trip number. Only the keys
// given are changed (null clears).
export async function setRideTripDetails(rideId, details) {
  const { data, error } = await supabase.rpc("ride_set_trip_details", { p_ride: rideId, p_details: details });
  if (error) throw error;
  return data || {};
}

// Settings > Ride vendors: the vendor's usual pickup window (minutes).
export async function setVendorWindow(vendorId, minutes) {
  const { data, error } = await supabase.rpc("ride_vendor_set_window", { p_id: vendorId, p_minutes: minutes == null || minutes === "" ? null : Number(minutes) });
  if (error) throw error;
  return data || {};
}

// The patient's preferred language (the interpreter default).
export async function fetchPreferredLanguage(patientId) {
  const { data, error } = await supabase.from("patients").select("preferred_language").eq("id", patientId).maybeSingle();
  if (error) throw error;
  return (data && data.preferred_language) || "";
}

// Active staff who can drive a care team ride, { id, full_name, role }.
export async function fetchDrivers() {
  const roles = ALL_DRIVER_ROLES;
  const { data, error } = await supabase.from("users").select("id, full_name, role")
    .in("role", roles).eq("is_active", true).order("full_name");
  if (error) throw error;
  return data || [];
}

// Minutes between pickup and the visit (Settings > Workflows; default 45).
export async function fetchRideLeadMinutes(practiceId) {
  const { data, error } = await supabase.from("practice_preferences").select("preferences").eq("practice_id", practiceId).maybeSingle();
  if (error) throw error;
  const v = Number(data && data.preferences && data.preferences.workflows && data.preferences.workflows.ride_lead_minutes);
  return Number.isFinite(v) && v >= 10 && v <= 180 ? v : 45;
}

// The care team driver can't make it: the ride becomes an NEMT request.
export async function driverCantGo(rideId, reason = "") {
  const { data, error } = await supabase.rpc("ride_driver_cant_go", { p_ride_id: rideId, p_reason: reason.trim() || null });
  if (error) throw error;
  return data;
}

export async function fetchRideForCareVisit(careVisitId) {
  // The driver's ride visit, or (20270111100000_ride_home_safety) the "Ride
  // home" visit of a driver who took over the ride home.
  const { data, error } = await supabase.from("appointment_rides").select(COLS + ", leave_by_at, drive_minutes, route_source, closed_reason, driver_ack, home_visit_id, home_miles, appointments(start_at, appt_type, status)")
    .or("care_visit_id.eq." + careVisitId + ",home_visit_id.eq." + careVisitId).limit(1).maybeSingle();
  if (error) throw error;
  return data || null;
}

// Ride markers for the calendar: { rides: {appointmentId: ride}, always: Set(patientId) }.
export async function fetchRideMarks(appts) {
  const ids = (appts || []).map((a) => a.id).filter(Boolean);
  const pids = Array.from(new Set((appts || []).map((a) => a.patient_id).filter(Boolean)));
  const out = { rides: {}, always: new Set() };
  if (!ids.length) return out;
  const [r, t] = await Promise.all([
    supabase.from("appointment_rides").select("appointment_id, status, provider_type, pickup_at").in("appointment_id", ids.slice(0, 400)),
    pids.length ? supabase.from("patient_transport").select("patient_id").eq("ride_need", "always").in("patient_id", pids.slice(0, 400)) : Promise.resolve({ data: [], error: null }),
  ]);
  if (r.error) throw r.error;
  if (t.error) throw t.error;
  for (const x of r.data || []) out.rides[x.appointment_id] = x;
  for (const x of t.data || []) out.always.add(x.patient_id);
  return out;
}

// ---------------------------------------------------------------------------
// Day of the visit (20261228100000_ride_day_of).
// ---------------------------------------------------------------------------
// The ride home: will_call | scheduled (with a time) | none | driver.
export async function setRideReturn(rideId, mode, atIso = null) {
  const { data, error } = await supabase.rpc("ride_set_return", { p_ride_id: rideId, p_mode: mode, p_at: atIso });
  if (error) throw error;
  return data;
}

// The visit was missed because the ride did not come (not a no-show).
export async function rideVisitMissed(appointmentId) {
  const { data, error } = await supabase.rpc("ride_visit_missed", { p_appointment_id: appointmentId });
  if (error) throw error;
  return data;
}

export async function fetchRideBoard(ymd) {
  const { data, error } = await supabase.rpc("ride_board", { p_date: ymd });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function fetchRideStats(days = 30) {
  const { data, error } = await supabase.rpc("ride_stats", { p_days: days });
  if (error) throw error;
  return data || {};
}

export async function setTransportPickup(patientId, address) {
  const { data, error } = await supabase.rpc("patient_transport_set_pickup", { p_patient_id: patientId, p_address: (address || "").trim() || null });
  if (error) throw error;
  return data;
}

// Rides given to a patient (chart card): booked rides for visits already past.
export async function fetchRideHistoryCount(patientId, days = 90) {
  const since = new Date(Date.now() - days * 86400000).toISOString();
  const { count, error } = await supabase.from("appointment_rides").select("id, appointments!inner(start_at)", { count: "exact", head: true })
    .eq("patient_id", patientId).eq("status", "booked").gte("appointments.start_at", since).lte("appointments.start_at", new Date().toISOString());
  if (error) throw error;
  return count || 0;
}

// Care team driver: Picked up / At the clinic / miles / undo
// (20261230100000_ride_insights). Returns the ride's tracking fields.
export async function rideDriverTrack(rideId, event, miles = null) {
  const { data, error } = await supabase.rpc("ride_driver_track", { p_ride_id: rideId, p_event: event, p_miles: miles });
  if (error) throw error;
  return data || {};
}

// How the ride went: on_time / late / did_not_come (null clears).
export async function setRideOutcome(rideId, outcome, minutesLate = null) {
  const { data, error } = await supabase.rpc("ride_set_outcome", { p_ride_id: rideId, p_outcome: outcome, p_minutes_late: minutesLate });
  if (error) throw error;
  return data || {};
}

// Reports > Rides (Owner, Manager, Front Desk).
export async function fetchRideInsights(days = 90) {
  const { data, error } = await supabase.rpc("ride_insights", { p_days: days });
  if (error) throw error;
  return data || {};
}

// ---- Round 2 (20270103100000_rides_round2) ----

// Day-before confirm: confirmed / no_record / rebooked (new trip number, pickup).
export async function confirmRide(rideId, status, { tripRef = null, pickupAt = null } = {}) {
  const { data, error } = await supabase.rpc("ride_confirm", { p_ride_id: rideId, p_status: status, p_trip_ref: tripRef, p_pickup_at: pickupAt });
  if (error) throw error;
  return data || {};
}

// Vendor directory (any staff can read; Owner / Manager edit).
export async function fetchRideVendors() {
  const { data, error } = await supabase.rpc("ride_vendors_list");
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}
export async function saveRideVendor({ id = null, name, phone = "", plans = [], active = true }) {
  const { data, error } = await supabase.rpc("ride_vendor_save", { p_id: id, p_name: name, p_phone: phone, p_plans: plans, p_active: active });
  if (error) throw error;
  return data || {};
}

// Mileage log (Owner / Manager): care team rides with miles and minutes, no patient names.
export async function fetchDriverLog(fromYmd, toYmd) {
  const { data, error } = await supabase.rpc("ride_driver_log", { p_from: fromYmd, p_to: toYmd });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

// ---- Driver alerts (20270105100000_ride_driver_alerts) ----------------------
// My driving: { sharing, is_driver, next: { pickup_at, leave_by_at, drive_minutes, route_source } }.
export async function fetchDriverStatus() {
  const { data, error } = await supabase.rpc("ride_driver_status");
  if (error) throw error;
  return data || { sharing: false, next: null };
}
export async function setLocationSharing(on) {
  const { data, error } = await supabase.rpc("staff_location_set_sharing", { p_on: !!on });
  if (error) throw error;
  return data;
}
// Kept by the database only while sharing is on and a ride is within 3 hours.
export async function reportLocation(lat, lon, accuracy = null) {
  const { data, error } = await supabase.rpc("staff_location_report", { p_lat: lat, p_lon: lon, p_accuracy: accuracy == null ? null : Math.round(accuracy) });
  if (error) throw error;
  return data;
}

// Pickup and clinic addresses for the ride's driver (or a supervisor). Audited
// in the database ('ride_directions').
export async function fetchRideDirections(rideId) {
  const { data, error } = await supabase.rpc("ride_driver_directions", { p_ride: rideId });
  if (error) throw error;
  return data || {};
}

// Set up a ride for one visit (Rides board). Returns { ride_id, status,
// provider_type } or throws with the reason (visit_not_open, visit_over, ...).
export async function rideSetUp(appointmentId, plan) {
  const { data, error } = await supabase.rpc("ride_set_up", { p_appt: appointmentId, p_plan: plan });
  if (error) throw error;
  return data || {};
}

// Change a care team ride's driver and/or pickup from the Rides board.
export async function rideCareTeamUpdate(rideId, { driverId = null, pickupAt = null } = {}) {
  const { data, error } = await supabase.rpc("ride_care_team_update", { p_ride: rideId, p_driver: driverId || null, p_pickup_at: pickupAt || null });
  if (error) throw error;
  return data || {};
}

// My recent ride alerts (driver; RLS: own rows only), newest first.
export async function fetchMyRideEvents(limit = 5) {
  const since = new Date(Date.now() - 2 * 86400000).toISOString();
  const { data, error } = await supabase.from("ride_driver_events").select("id, event, push_text, created_at")
    .gte("created_at", since).order("created_at", { ascending: false }).limit(limit);
  if (error) throw error;
  return data || [];
}

// A ride home for a patient who is already at the clinic (Rides board, Add a
// ride). Creates the "Ride home needed" task and a ride to book. Throws
// visit_not_under_way or ride_exists.
export async function rideReturnOnly(appointmentId) {
  const { data, error } = await supabase.rpc("ride_return_only", { p_appt: appointmentId });
  if (error) throw error;
  return data || {};
}

// Portal: "Cancel my ride there and back" (the whole ride). Throws not_open, no_ride, already_picked_up.
export async function portalRideCancel(appointmentId) {
  const { data, error } = await supabase.rpc("portal_ride_cancel", { p_appointment_id: appointmentId });
  if (error) throw error;
  return data || {};
}

// The driver answers a care team ride (20270110100000_ride_driver_accept):
// "accept", "hand_to" (another CHW or care manager, driverId), "nemt_self"
// (the driver books the Medicaid ride; the Ride help task is theirs) or
// "front_desk" (a Medicaid ride request for the Front Desk). Only the ride's
// driver. Throws not_your_ride, ride_not_open, bad_driver, already_picked_up.
export async function rideDriverRespond(rideId, action, { driverId = null, reason = "" } = {}) {
  const { data, error } = await supabase.rpc("ride_driver_respond", {
    p_ride: rideId, p_action: action, p_driver: driverId || null, p_reason: (reason || "").trim() || null,
  });
  if (error) throw error;
  return data || {};
}

// No ride home needed (20270111100000_ride_home_safety): reason hospital,
// admitted, family, own. Staff (not Billing) or the driver. Throws
// already_home, ride_not_open.
export async function rideHomeNotNeeded(rideId, reason) {
  const { data, error } = await supabase.rpc("ride_home_not_needed", { p_ride: rideId, p_reason: reason });
  if (error) throw error;
  return data || {};
}

// Portal, visit day: someone else is taking me home.
export async function portalRideHomeNotNeeded(appointmentId) {
  const { data, error } = await supabase.rpc("portal_ride_home_not_needed", { p_appointment_id: appointmentId });
  if (error) throw error;
  return data || {};
}

// Block 3: run an NCTracks check for a Medicaid ride's visit month unless one
// ran in the last 30 days. Best effort: the ride stands either way.
export async function ensureRideEligibility({ patientId, appointmentId, apptDate, force = false }) {
  if (!patientId || !appointmentId) return null;
  if (!force) {
    const { data, error } = await supabase.rpc("visit_eligibility", { p_appointment_ids: [appointmentId] });
    if (error) throw error;
    const row = (data || [])[0];
    if (row && row.medicaid === false) return null;
    if (row && row.checked_at && Date.now() - new Date(row.checked_at).getTime() < 30 * 86400000
        && row.status !== "Error" && row.status !== "Pending") return null;
  }
  return runEligibilityCheck({ patientId, appointmentId, serviceDate: apptDate || null });
}

// Rides block 4: is a Medicaid ride for this visit date short notice for the
// patient's plan? { plan_label, notice_days, need_by, short }
export async function fetchRideNotice(patientId, apptDate) {
  const { data, error } = await supabase.rpc("ride_notice", { p_patient: patientId, p_appt_date: apptDate });
  if (error) throw error;
  return data || null;
}

// ---- Rides block 7 (20270120100000_ride_block7) ----------------------------
// Standing ride order on the Transportation card: { vendor, ref, until } or {} to clear.
export async function setTransportStanding(patientId, standing) {
  const { data, error } = await supabase.rpc("patient_transport_set_standing", { p_patient_id: patientId, p_standing: standing || {} });
  if (error) throw error;
  return data || {};
}
// Same-day rides: the later visit rides on the earlier visit's trip.
export async function rideCombine(rideId, intoRideId) {
  const { data, error } = await supabase.rpc("ride_combine", { p_ride: rideId, p_into: intoRideId });
  if (error) throw error;
  return data || {};
}
// The office is closed on a day: cancel its rides (and text the patients).
export async function rideCloseDay(ymd, reason) {
  const { data, error } = await supabase.rpc("ride_close_day", { p_date: ymd, p_reason: reason || null });
  if (error) throw error;
  return data || {};
}

// ---- Rides block 8 (20270121100000_ride_block8) ----------------------------
// Ride records: an incident, a vendor complaint or a call to the vendor.
export async function addRideRecord(rideId, kind, subtype, { notes = "", injury = false } = {}) {
  const { data, error } = await supabase.rpc("ride_record_add", { p_ride: rideId, p_kind: kind, p_subtype: subtype, p_notes: notes || null, p_injury: !!injury });
  if (error) throw error;
  return data || {};
}
// One ride's records, or (rideId null, Owner / Manager) the practice's incidents and complaints.
export async function fetchRideRecords(rideId = null, days = 90) {
  const { data, error } = await supabase.rpc("ride_records_list", { p_ride: rideId, p_days: days });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}
export async function setRideRecordStatus(id, status) {
  const { data, error } = await supabase.rpc("ride_record_set_status", { p_id: id, p_status: status });
  if (error) throw error;
  return data || {};
}
// Mileage: "approved" (Owner / Manager), "paid" (also Billing), "none" to clear.
export async function setRideMiles(rideIds, status) {
  const { data, error } = await supabase.rpc("ride_miles_set", { p_ride_ids: rideIds, p_status: status });
  if (error) throw error;
  return data || {};
}
export async function fetchAcceptStats(days = 90) {
  const { data, error } = await supabase.rpc("ride_accept_stats", { p_days: days });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}
export async function fetchRideWeek(startYmd) {
  const { data, error } = await supabase.rpc("ride_board_week", { p_start: startYmd });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}
// Other ride options when it isn't Medicaid (no patient data).
export async function fetchRideResources() {
  const { data, error } = await supabase.rpc("ride_resources_list");
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}
export async function saveRideResource({ id = null, name, phone = "", url = "", notes = "", active = true }) {
  const { data, error } = await supabase.rpc("ride_resource_save", { p_id: id, p_name: name, p_phone: phone || null, p_url: url || null, p_notes: notes || null, p_active: active });
  if (error) throw error;
  return data || {};
}
// Portal: rides to rate, rating one, and the practice's other ride options.
export async function fetchPortalRideFeedbackDue(patientId) {
  const { data, error } = await supabase.rpc("portal_ride_feedback_due", { p_patient_id: patientId });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}
export async function sendPortalRideFeedback(appointmentId, rating, comment = "") {
  const { data, error } = await supabase.rpc("portal_ride_feedback", { p_appointment_id: appointmentId, p_rating: rating, p_comment: comment || null });
  if (error) throw error;
  return data || {};
}
export async function fetchPortalRideOptions(patientId) {
  const { data, error } = await supabase.rpc("portal_ride_options", { p_patient_id: patientId });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}
